import React, { useState } from 'react'
import {
  Flex,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  Button,
  VStack,
  HStack,
  Badge,
  Input,
  InputGroup,
  InputLeftElement,
  IconButton,
  Tooltip,
  useToast,
  Icon,
  Select,
} from '@chakra-ui/react'
import Card from 'components/card/Card'
import { SkeletonTable } from 'components/skeleton/Skeleton'
import { AiOutlineSearch } from 'react-icons/ai'
import { MdRefresh, MdPlaylistAddCheck, MdCancel, MdAccessTime } from 'react-icons/md'
import { useGetWaitlists, useCancelWaitlistEntry } from 'queries/waitlist.query'

export default function WaitlistTable() {
  const [page, setPage] = useState(1)
  const [perPage] = useState(20)
  const [statusFilter, setStatusFilter] = useState('')
  const [search, setSearch] = useState('')

  const textColor = useColorModeValue('secondaryGray.900', 'white')
  const textColorSecondary = useColorModeValue('secondaryGray.600', 'secondaryGray.400')
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100')
  const TheadBackground = useColorModeValue('white', 'gray.800')
  const hoverBackgroundColor = useColorModeValue('gray.50', 'gray.700')

  const toast = useToast()

  const { data: responseData, isLoading, refetch, isFetching } = useGetWaitlists(
    page,
    perPage,
    '',
    statusFilter,
    search
  )

  const cancelMutation = useCancelWaitlistEntry()

  const entries = responseData?.data?.data || []
  const pagination = responseData?.data || {}

  const handleCancelEntry = async (entry) => {
    if (!window.confirm(`Are you sure you want to cancel the waitlist entry for ${entry.customer_name}?`)) {
      return
    }

    try {
      await cancelMutation.mutateAsync(entry.id)
      toast({
        title: 'Waitlist Entry Cancelled',
        status: 'info',
        duration: 4000,
        isClosable: true,
      })
    } catch (err) {
      toast({
        title: 'Cancellation Failed',
        description: err?.response?.data?.message || err.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    }
  }

  const getStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case 'waiting':
        return <Badge colorScheme="orange" px={2.5} py={0.5} borderRadius="full">Waiting in Queue</Badge>
      case 'offered':
        return <Badge colorScheme="blue" px={2.5} py={0.5} borderRadius="full">Offer Dispatched</Badge>
      case 'converted':
        return <Badge colorScheme="green" px={2.5} py={0.5} borderRadius="full">Converted / Purchased</Badge>
      case 'expired':
        return <Badge colorScheme="gray" px={2.5} py={0.5} borderRadius="full">Offer Expired</Badge>
      case 'cancelled':
        return <Badge colorScheme="red" px={2.5} py={0.5} borderRadius="full">Cancelled</Badge>
      default:
        return <Badge px={2.5} py={0.5} borderRadius="full">{status}</Badge>
    }
  }

  return (
    <Card direction="column" w="100%" px="0px" overflowX={{ sm: 'scroll', lg: 'hidden' }}>
      {/* Header bar */}
      <Flex px="25px" justify="space-between" mb="20px" align="center" direction={{ base: 'column', md: 'row' }} gap={4}>
        <VStack align="flex-start" spacing={1}>
          <HStack spacing={2.5}>
            <Icon as={MdPlaylistAddCheck} w="24px" height="24px" color="amber.500" />
            <Text color={textColor} fontSize="22px" fontWeight="700" lineHeight="100%">
              Event Waitlist Queue
            </Text>
          </HStack>
          <Text color={textColorSecondary} fontSize="sm">
            Manage patrons queued for sold-out events and monitor automatic seat cascade offers.
          </Text>
        </VStack>

        <HStack spacing={3} wrap="wrap">
          <InputGroup w={{ base: '100%', md: '240px' }} size="sm">
            <InputLeftElement pointerEvents="none">
              <AiOutlineSearch color="gray.300" />
            </InputLeftElement>
            <Input
              borderRadius="12px"
              placeholder="Search name, email, phone..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
                setPage(1)
              }}
            />
          </InputGroup>

          <Select
            size="sm"
            w="160px"
            borderRadius="12px"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value)
              setPage(1)
            }}
          >
            <option value="">All Statuses</option>
            <option value="waiting">Waiting</option>
            <option value="offered">Offered</option>
            <option value="converted">Converted</option>
            <option value="expired">Expired</option>
            <option value="cancelled">Cancelled</option>
          </Select>

          <IconButton
            size="sm"
            aria-label="Refresh"
            icon={<MdRefresh />}
            borderRadius="10px"
            isLoading={isFetching}
            onClick={() => refetch()}
          />
        </HStack>
      </Flex>

      {/* Table Content */}
      {isLoading ? (
        <SkeletonTable />
      ) : entries.length === 0 ? (
        <Flex justify="center" align="center" direction="column" py="60px">
          <Icon as={MdAccessTime} w="48px" height="48px" color="gray.300" mb={3} />
          <Text fontSize="lg" fontWeight="semibold" color={textColor}>
            No Waitlist Entrants Found
          </Text>
          <Text fontSize="xs" color={textColorSecondary} mt={1}>
            {search || statusFilter ? 'Try clearing filters or search terms.' : 'Waitlist queues are currently empty.'}
          </Text>
        </Flex>
      ) : (
        <Table variant="simple" color="gray.500" mb="24px">
          <Thead bg={TheadBackground}>
            <Tr>
              <Th borderColor={borderColor} color="gray.400" fontSize="11px">QUEUE ID / DATE</Th>
              <Th borderColor={borderColor} color="gray.400" fontSize="11px">CUSTOMER</Th>
              <Th borderColor={borderColor} color="gray.400" fontSize="11px">EVENT</Th>
              <Th borderColor={borderColor} color="gray.400" fontSize="11px">CATEGORY & QTY</Th>
              <Th borderColor={borderColor} color="gray.400" fontSize="11px">STATUS</Th>
              <Th borderColor={borderColor} color="gray.400" fontSize="11px" textAlign="right">ACTIONS</Th>
            </Tr>
          </Thead>
          <Tbody>
            {entries.map((entry) => (
              <Tr key={entry.id} _hover={{ bg: hoverBackgroundColor }} transition="background 0.2s">
                <Td borderColor={borderColor}>
                  <Text fontSize="xs" fontWeight="bold" color={textColor}>
                    #{entry.id}
                  </Text>
                  <Text fontSize="10px" color={textColorSecondary}>
                    {entry.created_at ? new Date(entry.created_at).toLocaleDateString() : '—'}
                  </Text>
                </Td>

                <Td borderColor={borderColor}>
                  <Text fontSize="xs" fontWeight="semibold" color={textColor}>
                    {entry.customer_name}
                  </Text>
                  <Text fontSize="10px" color={textColorSecondary}>
                    {entry.customer_email} {entry.customer_phone ? `• ${entry.customer_phone}` : ''}
                  </Text>
                </Td>

                <Td borderColor={borderColor}>
                  <Text fontSize="xs" fontWeight="bold" color={textColor} noOfLines={1}>
                    {entry.event?.title || `Event #${entry.event_id}`}
                  </Text>
                </Td>

                <Td borderColor={borderColor}>
                  <Badge colorScheme="purple" fontSize="10px">
                    {entry.ticket_tier?.name || 'Any Category'}
                  </Badge>
                  <Text fontSize="10px" color={textColorSecondary} mt={0.5}>
                    {entry.tickets_requested} {entry.tickets_requested === 1 ? 'ticket' : 'tickets'}
                  </Text>
                </Td>

                <Td borderColor={borderColor}>
                  {getStatusBadge(entry.status)}
                  {entry.status === 'offered' && entry.offer_expires_at && (
                    <Text fontSize="9px" color="blue.500" mt={0.5}>
                      Expires: {new Date(entry.offer_expires_at).toLocaleTimeString()}
                    </Text>
                  )}
                </Td>

                <Td borderColor={borderColor} textAlign="right">
                  {entry.status === 'waiting' || entry.status === 'offered' ? (
                    <Tooltip label="Cancel waitlist entry">
                      <IconButton
                        size="xs"
                        colorScheme="red"
                        variant="ghost"
                        icon={<MdCancel />}
                        aria-label="Cancel entry"
                        onClick={() => handleCancelEntry(entry)}
                        isLoading={cancelMutation.isPending}
                      />
                    </Tooltip>
                  ) : (
                    <Text fontSize="11px" color={textColorSecondary}>—</Text>
                  )}
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      )}

      {/* Pagination Footer */}
      {pagination.total > perPage && (
        <Flex px="25px" py="15px" justify="space-between" align="center" borderTop="1px solid" borderColor={borderColor}>
          <Text fontSize="xs" color={textColorSecondary}>
            Showing {pagination.from || 0} to {pagination.to || 0} of {pagination.total || 0} entries
          </Text>
          <HStack spacing={2}>
            <Button
              size="xs"
              isDisabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              borderRadius="8px"
            >
              Previous
            </Button>
            <Text fontSize="xs" fontWeight="bold" px={2}>
              Page {page} of {pagination.last_page || 1}
            </Text>
            <Button
              size="xs"
              isDisabled={page >= (pagination.last_page || 1)}
              onClick={() => setPage((p) => p + 1)}
              borderRadius="8px"
            >
              Next
            </Button>
          </HStack>
        </Flex>
      )}
    </Card>
  )
}
